import { createRoot } from "react-dom/client";

import Select from "react-select";
import Api from "../../common/api";
import { extensions, MenuBar } from "../WysiwygEditor";
import { EditorProvider } from "@tiptap/react";
import { useEffect, useState } from "react";
import Loading from "../../boards/components/common/Loading";

interface JobRoleInputElements {
  primaryLocation: HTMLInputElement;
  associatedLocations: HTMLInputElement;
  description: HTMLTextAreaElement;
}

export function EditJobRole({ inputs }: { inputs: JobRoleInputElements }) {
  const { primaryLocation, associatedLocations, description } = inputs;

  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);

  const [defaultPrimaryLocation, setDefaultPrimaryLocation] = useState(null);
  const [defaultAssociatedLocations, setDefaultAssociatedLocations] = useState(
    [],
  );

  useEffect(() => {
    new Api().list("locations").then((locations) => {
      locations = locations.map((l) => ({
        value: l.id.toString(),
        label: l.title,
      }));

      setLocations(locations);

      let existing = locations.filter((l) => l.value === primaryLocation.value);
      if (existing) {
        setDefaultPrimaryLocation(existing[0]);
      }

      existing = associatedLocations.value.split(",");
      setDefaultAssociatedLocations(
        locations.filter((l) => existing.includes(l.value)),
      );

      setLoading(false);
    });
  }, []);

  return loading ? (
    <Loading className="my-2" />
  ) : (
    <>
      <h3>Locations</h3>

      <div className="form-group">
        <label>Primary Location</label>
        <Select
          defaultValue={defaultPrimaryLocation}
          options={locations}
          className="react-select"
          classNamePrefix="react-select"
          onChange={(newValue) => {
            if (newValue) {
              primaryLocation.value = newValue.value;
            } else {
              primaryLocation.value = "";
            }
          }}
        />
      </div>

      <div className="form-group">
        <label>Associated Locations</label>
        <Select
          isMulti
          defaultValue={defaultAssociatedLocations}
          options={locations}
          className="react-select"
          classNamePrefix="react-select"
          onChange={(newValue) => {
            associatedLocations.value = newValue.map((v) => v.value).join(",");
          }}
        />
      </div>

      <h3>Description</h3>

      <div className="wysiwyg-editor">
        <div className="px-4 py-3">
          <EditorProvider
            slotBefore={<MenuBar />}
            extensions={extensions}
            editorProps={{
              attributes: {
                class: "wysiwyg text-white",
              },
            }}
            content={description.value}
            onUpdate={({ editor }) => {
              description.value = editor.getHTML();
              description.dispatchEvent(new Event("change"));
            }}
          ></EditorProvider>
        </div>
      </div>
    </>
  );
}

export default function loadJobRoleEditor(el: Element) {
  const inputs: JobRoleInputElements = {
    primaryLocation: el.querySelector(
      `input[name="job_role[primary_location_id]"]`,
    ),
    associatedLocations: el.querySelector(
      `input[name="job_role[location_ids]"]`,
    ),
    description: el.querySelector("textarea"),
  };

  // Avoid re-initializing
  if (!el.querySelector("div")) {
    const parentNode = document.createElement("div");
    el.appendChild(parentNode);

    const root = createRoot(parentNode);
    root.render(<EditJobRole inputs={inputs} />);
  }
}
